/** Canonical mainnet keys accepted by the authenticated preferences endpoint. */
export const HOME_NETWORKS = [
  'bitcoin',
  'ethereum',
  'bsc',
  'base',
  'hyperevm',
  'tron',
  'solana',
  'sui',
] as const;

export type HomeNetwork = (typeof HOME_NETWORKS)[number];

export interface HomeNetworkPreference {
  homeNetwork: HomeNetwork | null;
  revision: number;
}

/** Validate a key at the HTTP or picker boundary; labels and families are invalid. */
export function isHomeNetwork(value: unknown): value is HomeNetwork {
  return typeof value === 'string' && HOME_NETWORKS.some((key) => key === value);
}

/** A missing field is an invalid response, not the explicit All-networks default. */
export function parseHomeNetworkPreference(value: unknown): HomeNetworkPreference {
  if (typeof value !== 'object' || value === null ||
      !('home_network' in value) || !('revision' in value)) {
    throw new Error('Invalid Home network preference response.');
  }
  const network = value.home_network;
  const revision = value.revision;
  if ((network === null || isHomeNetwork(network)) &&
      typeof revision === 'number' && Number.isSafeInteger(revision) && revision >= 0) {
    return { homeNetwork: network, revision };
  }
  throw new Error('Invalid Home network preference response.');
}

/**
 * This preference names mainnets. Developer Mode retains the preference but
 * displays all testnet networks; Ethereum must never mean every EVM testnet.
 */
export function homeNetworkForTier(
  network: HomeNetwork | null,
  isTestnet: boolean,
): HomeNetwork | null {
  return isTestnet ? null : network;
}

/** Filter by the row's actual network, never a wallet family or display icon. */
export function filterHomeNetwork<T extends { chain: string; networkChainKey?: string }>(
  rows: T[],
  network: HomeNetwork | null,
): T[] {
  return network === null ? rows : rows.filter((row) => (row.networkChainKey ?? row.chain) === network);
}
